import { blogArticles, type BlogArticle } from './blog-data'

const TAG_VARIANTS: Record<string, string[]> = {
  'tesla-lock-sound': ['tesla lock sound', 'lock sound', 'custom lock sound', 'custom lock sounds', 'custom sounds'],
  'model-3': ['model 3', 'model-3'],
  'model-y': ['model y', 'model-y'],
  'cybertruck': ['cybertruck'],
  'installation': ['installation', 'how-to', 'how to', 'how to install', 'tutorial'],
  'lockchime-wav': ['lockchime.wav', 'lockchime', 'lockchhime'],
  'boombox': ['boombox', 'tesla boombox'],
  'guide': ['guide'],
  'wav': ['wav'],
  'usb': ['usb', 'usb drive', 'usb format', 'usb-c'],
  'troubleshooting': ['troubleshooting', 'fix', 'not working', 'custom lock sound not working', 'help', 'problems', 'solutions'],
  'free-downloads': ['free downloads', 'free', 'download', 'lock sound download', 'tesla lock sounds download'],
  'setup': ['setup', 'beginner'],
  'model-s': ['model s', 'model-s'],
  'model-x': ['model x', 'model-x'],
  'firmware': ['firmware', 'software-update', 'software update', 'update', 'ota update'],
  'sci-fi': ['sci-fi', 'sci fi'],
  'movies': ['movies', 'film', 'tv', 'cinematic'],
  'viral': ['viral', 'trending', 'popular', 'memes', 'meme'],
  'volume': ['volume', 'loud', 'quiet', 'too-loud', 'boost', 'lower', 'reduce'],
  'gaming': ['gaming', 'video-games', 'video games'],
  'tiktok': ['tiktok'],
  'comparison': ['comparison', 'compare', 'vs'],
  'retro': ['retro', '8-bit', '8bit'],
  'holiday': ['holiday', 'halloween', 'christmas'],
  'star-wars': ['star wars', 'lightsaber', 'darth vader', 'imperial march', 'r2-d2'],
  'horror': ['horror', 'horror-gaming', 'spooky', 'scary', 'creepy'],
  'anime': ['anime', 'japanese'],
  'nintendo': ['nintendo', 'mario', 'zelda', 'pokemon', 'pikachu'],
}

const TAG_DISPLAY_NAMES: Record<string, string> = {
  'tesla-lock-sound': 'Tesla Lock Sound',
  'model-3': 'Model 3',
  'model-y': 'Model Y',
  'cybertruck': 'Cybertruck',
  'installation': 'Installation',
  'lockchime-wav': 'LockChime.wav',
  'boombox': 'Boombox',
  'guide': 'Guide',
  'wav': 'WAV',
  'usb': 'USB',
  'troubleshooting': 'Troubleshooting',
  'free-downloads': 'Free Downloads',
  'setup': 'Setup',
  'model-s': 'Model S',
  'model-x': 'Model X',
  'firmware': 'Firmware',
  'sci-fi': 'Sci-Fi',
  'movies': 'Movies & TV',
  'viral': 'Viral & Trending',
  'volume': 'Volume',
  'gaming': 'Gaming',
  'tiktok': 'TikTok',
  'comparison': 'Comparison',
  'retro': 'Retro',
  'holiday': 'Holiday',
  'star-wars': 'Star Wars',
  'horror': 'Horror',
  'anime': 'Anime',
  'nintendo': 'Nintendo',
}

const rawToSlug = new Map<string, string>()
for (const [slug, variants] of Object.entries(TAG_VARIANTS)) {
  for (const v of variants) {
    rawToSlug.set(v.toLowerCase(), slug)
  }
}

export function normalizeTagToSlug(rawTag: string): string {
  return rawToSlug.get(rawTag.toLowerCase()) ?? rawTag.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')
}

export function getTagDisplayName(slug: string): string {
  return TAG_DISPLAY_NAMES[slug] ?? slug.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
}

export function getArticlesByTagSlug(slug: string): BlogArticle[] {
  return blogArticles.filter(article =>
    article.tags.some(tag => normalizeTagToSlug(tag) === slug)
  )
}

export function getAllTagSlugsWithCounts(): { slug: string; displayName: string; count: number }[] {
  const counts = new Map<string, number>()
  for (const article of blogArticles) {
    const seen = new Set<string>()
    for (const tag of article.tags) {
      const slug = normalizeTagToSlug(tag)
      if (!seen.has(slug)) {
        seen.add(slug)
        counts.set(slug, (counts.get(slug) ?? 0) + 1)
      }
    }
  }
  return Array.from(counts.entries())
    .map(([slug, count]) => ({ slug, displayName: getTagDisplayName(slug), count }))
    .sort((a, b) => b.count - a.count)
}

export const MIN_ARTICLES_FOR_TAG_PAGE = 5

/**
 * Tags broad enough that their page is the /blog index with a different H1.
 * They stay reachable (chips, internal links) but are noindex. Model and
 * topic tags carry a distinct intent and stay indexable.
 */
const GENERIC_TAG_SLUGS: ReadonlySet<string> = new Set(['2026', 'guide', 'usb', 'installation'])

export function isGenericTagSlug(slug: string): boolean {
  return GENERIC_TAG_SLUGS.has(slug)
}

export function getIndexableTagSlugs(): string[] {
  return getAllTagSlugsWithCounts().flatMap(t =>
    t.count >= MIN_ARTICLES_FOR_TAG_PAGE && !isGenericTagSlug(t.slug) ? [t.slug] : []
  )
}
